import React, { useEffect, useState, useMemo } from "react";
import styles from "./BundlesHero.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";

import BestSellers from "components/Heros/BestSellers/BestSellers";
import { getGameList } from "constants/GameListAdapter";
import BundleGameCard from "components/Heros/BundleCard/BundleCard";
import PricingFunctions from "common/PricingFunctions";
import { addMultipleToCart } from "actions/cart";

const filters = ["game-show", "party-game"];


const BundlesHero = () => {
    const [selectedOption, setSelectedOption] = useState(0);
    const [games, setGames] = useState([]);
    const [bundlePrice, setBundlePrice] = useState(0);
    const [originalPrice, setOriginalPrice] = useState(0);
    const [saveAmount, setSaveAmount] = useState(0);
    const [isPulsing, setIsPulsing] = useState(false);

    // ⬇️ selections stored per filter
    const [selectedGamesByOption, setSelectedGamesByOption] = useState({
        0: [],
        1: [],
    });

    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const pricing = useSelector((state) => state.pricing);
    const { isLoggedIn, user } = useSelector((state) => state.auth);

    const now = new Date();
    const isChristmas = now.getMonth() === 11 && (now.getDate() < 26 || (now.getDate() === 26 && now.getHours() < 8));

    const discountPercentage = 15;
    const discountCode = "EASTER15";
    const countryCode = pricing.countryCode;
    const currency = PricingFunctions.getCurrencySymbol(countryCode);

    // ⬇️ currently active selection
    const selectedGames = selectedGamesByOption[selectedOption] || [];
    const hasSelection = selectedGames.length > 0;
    console.log("selected games : ", selectedGames);

    const ownedGameIds = useMemo(() => {
        if (!isLoggedIn || !user?.games) return new Set();
        return new Set(user.games.map(g => g.code));
    }, [isLoggedIn, user]);

    const availableGameShowGames = useMemo(() => {
        // Filter by "game-show" tag, rely on API order (DisplayOrder)
        return getGameList().filter(g =>
            g.status === "enabled" &&
            g.tags?.includes("game-show") &&
            (!isLoggedIn || !ownedGameIds.has(g.gameId))
        ).slice(0, 4);
    }, [isLoggedIn, ownedGameIds]);

    const availablePartyGames = useMemo(() => {
        // Filter by "party-game" tag, rely on API order (DisplayOrder)
        return getGameList().filter(g =>
            g.status === "enabled" &&
            g.tags?.includes("party-game") &&
            (!isLoggedIn || !ownedGameIds.has(g.gameId))
        ).slice(0, 4);
    }, [isLoggedIn, ownedGameIds]);

    useEffect(() => {
        if (availableGameShowGames.length < 2) {
            setSelectedOption(1); // party games
        }
    }, [availableGameShowGames.length]);

    // ⬇️ initialize selections (all games selected per list)
    useEffect(() => {
        setSelectedGamesByOption({
            0: availableGameShowGames.map(g => g.gameId),
            1: availablePartyGames.map(g => g.gameId),
        });
    }, [availableGameShowGames, availablePartyGames]);

    useEffect(() => {
        if (selectedOption === 0) {
            setGames(availableGameShowGames);
        } else {
            setGames(availablePartyGames);
        }
    }, [selectedOption, availableGameShowGames, availablePartyGames]);

    // ⬇️ calculate bundle price
    useEffect(() => {
        let bundleTotal = 0;
        let total = 0;
        let savings = 0;

        games.forEach((game) => {
            if (selectedGames.includes(game.gameId)) {
                const basePrice =
                    PricingFunctions.getGamePricing(countryCode, game)?.raw || 0;

                bundleTotal += basePrice * (1 - discountPercentage / 100);
                total += basePrice;
            }
        });
        savings = total - bundleTotal;
        setSaveAmount(savings);
        setOriginalPrice(total);
        setBundlePrice(bundleTotal);
    }, [selectedGames, games, countryCode]);

    // ⬇️ toggle selection for current list
    const toggleSelected = (gameId) => {
        // Trigger pulse animation
        setIsPulsing(true);
        setTimeout(() => setIsPulsing(false), 300);

        setSelectedGamesByOption((prev) => {
            const current = prev[selectedOption] || [];

            return {
                ...prev,
                [selectedOption]: current.includes(gameId)
                    ? current.filter((id) => id !== gameId)
                    : [...current, gameId],
            };
        });
    };

    const handleAddToCart = () => {
        if (!selectedGames.length) return;

        if (discountCode) {
            const updatedParams = new URLSearchParams(searchParams);
            updatedParams.set("discountCode", discountCode);
            setSearchParams(updatedParams);
        }

        // only add games that are visible (not owned)
        const visibleGameIds = new Set(games.map(g => g.gameId));

        const gamesToAdd = selectedGames
            .filter(gId => visibleGameIds.has(gId))
            .map(gId => getGameList().find(x => x.gameId === gId))
            .filter(Boolean);

        if (gamesToAdd.length) {
            dispatch(addMultipleToCart(gamesToAdd));
        }
    };

    if (availablePartyGames.length < 2) {
        return <BestSellers />;
    }

    const showBundleButtons =
        availableGameShowGames.length >= 2 &&
        availablePartyGames.length >= 2;

    return (
        <div className={styles.bundlesContainer}>
            <h4 className={styles.title}>
                Glow up your party with our game bundles!
            </h4>

            {showBundleButtons && (
                <div className={styles.buttons}>
                    <button
                        className={`${styles.customButton} ${selectedOption === 0 ? styles.selected : ""
                            }`}
                        onClick={() => setSelectedOption(0)}
                    >
                        Game Shows
                    </button>

                    <button
                        className={`${styles.customButton} ${selectedOption === 1 ? styles.selected : ""
                            }`}
                        onClick={() => setSelectedOption(1)}
                    >
                        Party Games
                    </button>
                </div>
            )}
            <div
                className={`row d-flex flex-direction-row justify-content-center align-items-center ${styles.games}`}
            >
                {games.map((g) => (
                    <BundleGameCard
                        key={g.gameId}
                        game={g}
                        showAddToCart={true}
                        discountCode={discountCode}
                        discountPercentage={discountPercentage}
                        noButton={true}
                        showBanner={true}
                        onClick={() => toggleSelected(g.gameId)}
                        selected={selectedGames.includes(g.gameId)}
                    />
                ))}
            </div>

            {hasSelection
                ? <p className={`${styles.priceText} ${isPulsing ? styles.pulse : ''}`}>
                    Grab this bundle for{' '}
                    {discountPercentage > 0 && (
                        <span className={styles.originalPriceStrike}>{currency}{originalPrice.toFixed(2)}</span>
                    )}
                    {' '}<span style={{ fontWeight: "bold" }}>only {currency}{bundlePrice.toFixed(2)}</span>
                </p>
                : <p className={styles.priceText}>Select at least one game</p>
            }

            <button
                className={`${styles.button} ${styles.redAlt} ${!hasSelection ? styles.disabled : ""
                    }`}
                onClick={handleAddToCart}
                disabled={!hasSelection}
            >
                ADD TO BASKET
            </button>

            {discountCode && (
                <p className={styles.discountNote}>Prices include {discountCode} discount</p>
            )}
            <p className={styles.discountNote}>
                <span className={styles.shopLink} onClick={() => navigate("/shop")}>Visit the main shop</span>
            </p>
        </div>
    );
};

export default BundlesHero;